package dev.lingvo.shared

/**
 * Which way a locale's script runs.
 *
 * Nothing in the editor set `dir` at all, which meant an Arabic target rendered left-to-right and a
 * source like `Delete %1$s?` inside a right-to-left paragraph re-ordered its placeholder and its
 * question mark on screen. **What the translator saw was not what would ship** — and no amount of
 * review workflow catches an error the reviewer is also shown incorrectly.
 */
enum class TextDirection(val html: String) {
    LTR("ltr"),
    RTL("rtl"),
}

/**
 * The right-to-left languages, by ISO 639-1 and the 639-3 codes that actually appear in the wild.
 *
 * Matched on the language subtag, so `ar`, `ar-EG` and `ar_SA` all resolve the same way — locale
 * codes arrive from config files, CLI flags and imported memories, and a list that only matched the
 * bare form would send half of them the wrong way with no error.
 */
private val RTL_LANGUAGES = setOf(
    "ar", // Arabic
    "arc", // Aramaic
    "ckb", // Sorani Kurdish
    "dv", // Divehi
    "fa", // Persian
    "ha", // Hausa (Ajami)
    "he", "iw", // Hebrew — `iw` is the deprecated code Java still emits
    "khw", // Khowar
    "ks", // Kashmiri
    "ps", // Pashto
    "sd", // Sindhi
    "ug", // Uyghur
    "ur", // Urdu
    "yi", // Yiddish
)

/** Which way this locale's text runs. Unknown locales are left-to-right, which is the safe default. */
fun textDirection(locale: String?): TextDirection {
    val language = locale?.trim()?.replace('_', '-')?.substringBefore('-')?.lowercase()
    return if (language in RTL_LANGUAGES) TextDirection.RTL else TextDirection.LTR
}

/** True when a locale needs the right-to-left treatment. */
fun isRtl(locale: String?): Boolean = textDirection(locale) == TextDirection.RTL

/**
 * Unicode's "first strong isolate" and its terminator.
 *
 * Wrapping a placeholder in these tells the bidirectional algorithm to treat the run as an
 * independent unit, so `%1$s` keeps its shape and stays put inside an Arabic sentence instead of
 * having its `%` and `s` visually separated by the surrounding text. Without them the translator is
 * shown a string that is not the string the app will render.
 */
const val FIRST_STRONG_ISOLATE = '⁨'
const val POP_DIRECTIONAL_ISOLATE = '⁩'

/**
 * Wraps every parsed placeholder in bidi isolates, for display only.
 *
 * **Never stored and never sent to a provider.** These are invisible formatting characters; writing
 * them into a translation would put them in the shipped app, where they would appear in a
 * `NSLocalizedString` lookup key, break an exact-match memory hit, and count against a length limit.
 * This exists to make a preview honest, not to change the text.
 */
fun isolatePlaceholders(text: String?): String {
    if (text.isNullOrEmpty()) return text.orEmpty()
    val found = Placeholders.extractAll(text)
    if (found.isEmpty()) return text
    return buildString {
        var cursor = 0
        // Sorted, because a caller may hand back overlapping or out-of-order spans and appending
        // them blind would scramble the very string this function exists to render faithfully.
        found.sortedBy { it.start }.forEach { placeholder ->
            if (placeholder.start < cursor) return@forEach
            append(text, cursor, placeholder.start)
            append(FIRST_STRONG_ISOLATE)
            append(text, placeholder.start, placeholder.end)
            append(POP_DIRECTIONAL_ISOLATE)
            cursor = placeholder.end
        }
        append(text, cursor, text.length)
    }
}

/**
 * The next placeholder in the source that the target has not used yet, in source order.
 *
 * Backs `Alt+P`. The parser has always existed and has only ever been used to *blame* — a
 * `PLACEHOLDER_MISMATCH` after the fact — when it could have been helping type the thing instead.
 *
 * Compared on [Placeholder.canonical] rather than the raw text, so `%1$s` and `%s` in equivalent
 * positions count as the same argument and a translator is not offered a duplicate they have
 * already placed in a different form.
 */
fun nextMissingPlaceholder(source: String?, target: String?): String? {
    val used = Placeholders.extractAll(target).map { it.canonical }.toMutableList()
    return Placeholders.extractAll(source).firstOrNull { placeholder ->
        // Removed as it matches, so a source with two `%s` offers the second one after the first is
        // placed rather than reporting itself satisfied.
        used.remove(placeholder.canonical).not()
    }?.raw
}
